/**
 * Распознаватель маркера в размеченном тексте.
 *
 * `pattern` описывает маркер целиком и является единственным источником его формата:
 * по нему маркер и ищется в тексте, и разбирается на составляющие. Флаги регулярки
 * не важны — утилита собирает свои.
 */
export type TextMarker<T> = {
  /** Шаблон маркера целиком, например `/\[(\d{2}:\d{2})\]/` */
  pattern: RegExp;
  /** Разбор совпадения (группы — из `pattern`) в значимую часть */
  parse: (match: RegExpMatchArray) => T;
};

/** Часть размеченного текста: обычный текст либо распознанный маркер. */
export type MarkedTextPart<T> = { kind: 'text'; text: string } | { kind: 'marker'; marker: T };

/** Ищет первый распознаватель, которому целиком соответствует фрагмент. */
const parseMarker = <T>(fragment: string, markers: TextMarker<T>[]): T | undefined => {
  for (const { pattern, parse } of markers) {
    const match = fragment.match(new RegExp(`^(?:${pattern.source})$`));
    if (match) return parse(match);
  }
  return undefined;
};

/**
 * Разбирает текст со служебными маркерами (таймкоды, ссылки на правила, подстановки
 * анонимизации и т. п.) на обычные фрагменты и распознанные маркеры.
 *
 * Распознаватели проверяются по порядку — выигрывает первый подошедший, поэтому более
 * частные шаблоны должны идти раньше общих. Фрагмент, не распознанный ни одним из них,
 * остаётся обычным текстом.
 */
export const parseMarkedText = <T>(text: string, markers: TextMarker<T>[]): MarkedTextPart<T>[] => {
  const searchPattern = new RegExp(markers.map(({ pattern }) => `(?:${pattern.source})`).join('|'), 'g');
  const parts: MarkedTextPart<T>[] = [];
  let cursor = 0;

  for (const match of text.matchAll(searchPattern)) {
    const marker = parseMarker(match[0], markers);
    if (marker === undefined) continue;

    if (match.index > cursor) parts.push({ kind: 'text', text: text.slice(cursor, match.index) });
    parts.push({ kind: 'marker', marker });
    cursor = match.index + match[0].length;
  }

  if (cursor < text.length) parts.push({ kind: 'text', text: text.slice(cursor) });

  return parts;
};
